import { UserProfile$ } from '@lemoncloud/codes-backend-api';
import { AWSWebCoreState } from '@lemoncloud/lemon-web-core';
import { create } from 'zustand';

import { LANGUAGE_KEY, webCore } from '../core';
export type UserProfile = any;
export type UserView = any;

export interface WebCoreState {
    initState?: AWSWebCoreState;
    isInitialized: boolean;
    isAuthenticated: boolean;
    error: Error | null;
    profile: UserProfile$ | null;
    userName: string;
    email: string;
    userId: string;
}

export interface WebCoreStore extends WebCoreState {
    initialize: () => void;
    logout: () => Promise<void>;
    setIsAuthenticated: (isAuth: boolean) => void;
    setProfile: (profile: UserProfile) => void;
    updateUserName: (user: UserView) => void;
    updateUserPhoto: (user: UserView) => void;
}

const initialState: Pick<WebCoreStore, keyof WebCoreState> = {
    initState: undefined,
    isInitialized: false,
    isAuthenticated: false,
    error: null,
    profile: null,
    userName: '',
    email: '',
    userId: '',
};

export const useWebCoreStore = create<WebCoreStore>()(set => ({
    ...initialState,
    /**
     * Initializes the web core application
     * - Sets up authentication state
     * - Configures language preferences
     * - Handles initialization errors
     */
    initialize: async () => {
        set({ isInitialized: false, error: null });
        const initState = await webCore.init();
        await webCore.setUseXLemonLanguage(true, LANGUAGE_KEY);
        const isAuthenticated = await webCore.isAuthenticated();

        set({ isInitialized: true, isAuthenticated, initState });
    },

    /**
     * Handles user logout
     * - Clears authentication state
     * - Removes user profile data
     * - Redirects to login page
     */
    logout: async () => {
        await webCore.logout();
        if (typeof window !== 'undefined') {
            localStorage.removeItem('user_profile');
        }
        set({ isAuthenticated: false, profile: null, userName: '' });
        window.location.href = '/auth/login';
    },

    /**
     * Updates authentication state
     * @param isAuthenticated - New authentication state
     */
    setIsAuthenticated: (isAuthenticated: boolean) => set({ isAuthenticated }),

    /**
     * Updates user profile information
     * @param profile - User profile data
     */
    setProfile: (profile: UserProfile) => {
        if (typeof window !== 'undefined') {
            localStorage.setItem('user_profile', JSON.stringify(profile));
        }

        set({
            profile,
            userName: profile.$user?.name || 'Unknown',
            email: profile.$user?.email || '',
            userId: profile.$user?.id || '',
        });
    },

    /**
     * Updates userName and related profile information
     * @param user - Updated user view data
     */
    updateUserName: (user: UserView) =>
        set(state => {
            const profile = { ...state.profile, $user: user } as UserProfile$;
            const userName = user.name;

            if (typeof window !== 'undefined') {
                localStorage.setItem('user_profile', JSON.stringify(profile));
            }

            return { ...state, profile, userName };
        }),

    /**
     * Updates userPhoto
     * @param user - Updated user view data
     */
    updateUserPhoto: (user: UserView) =>
        set(state => {
            const profile = { ...state.profile, $user: { ...user } } as UserProfile$;

            if (typeof window !== 'undefined') {
                localStorage.setItem('user_profile', JSON.stringify(profile));
            }

            return { ...state, profile };
        }),
}));
